import { MailDetailLayout } from "../MailDetailLayout";
/**
 * Componente ridisegnato per il dettaglio di un thread/case.
 * Include sidebar, timeline visuale, info paziente con specie, breadcrumb.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { casesApi, CaseDetail, Attachment } from "../../api/cases";
import { EmailItem } from "../EmailItem";
import { ThreadTimeline } from "../ThreadTimeline";
import { ImagingPanel } from "../ImagingPanel";
import { VetAssignmentModal } from "../VetAssignmentModal";
import { SkeletonText } from "../Skeleton";
import { useChatStore } from "../../store/chatStore";
import CaseAuditTrailModal from "../CaseAuditTrailModal";
import { Button } from "@/components/ui/button";
import {
  UserPlus,
  CircleCheck,
  HeartPulse,
  MessageSquare,
  Scan,
  ClipboardList,
  XCircle,
  Dog,
  Cat,
  PawPrint,
  Fish,
  Bird,
  Rabbit,
  Mail,
  Phone,
  User,
  GitBranch,
} from "lucide-react";

interface PatientCardProps {
  patientName: string | null;
  patientSpecies: string | null;
  patientBreed: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
}

function SpeciesIcon({ species }: { species: string | null }) {
  if (!species)
    return <PawPrint className="size-icon-ui text-muted-foreground" />;
  const s = species.toLowerCase();
  if (s.includes("cane") || s.includes("dog"))
    return <Dog className="size-icon-ui text-amber-600" />;
  if (s.includes("gatto") || s.includes("cat"))
    return <Cat className="size-icon-ui text-amber-500" />;
  if (s.includes("pesce") || s.includes("fish"))
    return <Fish className="size-icon-ui text-blue-500" />;
  if (s.includes("uccello") || s.includes("bird"))
    return <Bird className="size-icon-ui text-green-500" />;
  if (s.includes("coniglio") || s.includes("rabbit"))
    return <Rabbit className="size-icon-ui text-pink-500" />;
  return <PawPrint className="size-icon-ui text-muted-foreground" />;
}

function PatientCard({
  patientName,
  patientSpecies,
  patientBreed,
  ownerName,
  ownerEmail,
  ownerPhone,
}: PatientCardProps) {
  return (
    <div className="bg-card rounded-lg border border-border p-4 mb-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
        <HeartPulse className="size-icon-inline" />
        Paziente
      </h3>

      <div className="flex items-center gap-3 mb-3">
        <div className="size-avatar-md rounded-lg bg-primary/10 flex items-center justify-center">
          <SpeciesIcon species={patientSpecies} />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">
            {patientName || "N/D"}
          </p>
          {(patientSpecies || patientBreed) && (
            <p className="text-xs text-muted-foreground">
              {patientSpecies}
              {patientBreed ? ` · ${patientBreed}` : ""}
            </p>
          )}
        </div>
      </div>

      <div className="pt-3 border-t border-border">
        <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Proprietario
        </h4>

        {ownerName && (
          <p className="text-sm text-foreground mb-1 flex items-center gap-1.5">
            <User className="size-icon-inline text-muted-foreground" />
            {ownerName}
          </p>
        )}

        {ownerEmail && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
            <Mail className="w-3 h-3" />
            {ownerEmail}
          </p>
        )}

        {ownerPhone && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Phone className="w-3 h-3" />
            {ownerPhone}
          </p>
        )}
      </div>
    </div>
  );
}

type ViewMode = "timeline" | "emails";

export function ThreadDetail({
  caseId: providedCaseId,
  embedded = false,
}: { caseId?: string; embedded?: boolean } = {}) {
  const { caseId: routeCaseId } = useParams<{ caseId: string }>();
  const caseId = providedCaseId || routeCaseId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAttachment, setSelectedAttachment] =
    useState<Attachment | null>(null);
  const [showVetModal, setShowVetModal] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("emails");
  const [showContext, setShowContext] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  const { setActiveCaseId, setScope } = useChatStore();

  const {
    data: caseData,
    isLoading,
    error,
  } = useQuery<CaseDetail>({
    queryKey: ["case", caseId],
    queryFn: () => casesApi.get(caseId!),
    enabled: !!caseId,
  });

  useEffect(() => {
    if (caseId) {
      setScope("thread");
    }
    return () => {
      setActiveCaseId(null);
      setScope("global");
    };
  }, [caseId, setActiveCaseId, setScope]);

  useEffect(() => {
    if (caseData && caseId) {
      setActiveCaseId(caseId, caseData.title || caseData.patient_name || null);
    }
  }, [caseData, caseId, setActiveCaseId]);

  const assignVetMutation = useMutation({
    mutationFn: ({ caseId, vetId }: { caseId: string; vetId: string }) =>
      casesApi.assignVet(caseId, vetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["mail-queue"] });
    },
  });

  const closeCaseMutation = useMutation({
    mutationFn: (caseId: string) => casesApi.closeCase(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["mail-queue"] });
    },
  });

  const handleAssignVet = () => setShowVetModal(true);

  const handleVetAssign = (vetId: string) => {
    if (caseId) {
      assignVetMutation.mutate(
        { caseId, vetId },
        {
          onSuccess: () => setShowVetModal(false),
        },
      );
    }
  };

  const handleCloseCase = () => {
    if (window.confirm("Sei sicuro di voler chiudere questo caso?") && caseId) {
      closeCaseMutation.mutate(caseId);
    }
  };

  const handleImageClick = (attachment: Attachment) => {
    setSelectedAttachment(attachment);
  };

  const handleEmailClick = (emailId: string) => {
    setSelectedEmailId(emailId);
    setViewMode("emails");
  };

  if (isLoading) {
    return (
      <MailDetailLayout
        embedded={embedded}
        breadcrumbs={[
          { label: "Triage", href: "/triage" },
          { label: "Caricamento..." },
        ]}
      >
        <SkeletonText lines={5} />
      </MailDetailLayout>
    );
  }

  if (error || !caseData) {
    return (
      <MailDetailLayout
        embedded={embedded}
        breadcrumbs={[
          { label: "Triage", href: "/triage" },
          { label: "Errore" },
        ]}
      >
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-danger mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">
              Errore nel caricamento del caso
            </p>
            <Button onClick={() => navigate("/")} variant="default">
              Torna alla home
            </Button>
          </div>
        </div>
      </MailDetailLayout>
    );
  }

  const allEmails = caseData.threads
    .flatMap((t) => t.emails)
    .sort((a, b) => {
      const dateA = new Date(a.received_at || a.created_at || 0);
      const dateB = new Date(b.received_at || b.created_at || 0);
      return dateA.getTime() - dateB.getTime();
    });

  const isAssigned = !!caseData.vet_id;
  const isClosed = caseData.status.toLowerCase() === "closed";
  const caseTitle = caseData.title || caseData.patient_name || "Caso";

  return (
    <MailDetailLayout
      embedded={embedded}
      breadcrumbs={[{ label: "Triage", href: "/triage" }, { label: caseTitle }]}
      logoIcon={<MessageSquare className="size-4 text-primary" />}
      topbarActions={
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={showContext}
            onClick={() => setShowContext(!showContext)}
          >
            <HeartPulse className="size-4" />
            Paziente
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAuditTrail(true)}
          >
            <GitBranch className="size-icon-inline" />
            Audit Trail
          </Button>
          <Button
            variant={isAssigned ? "secondary" : "default"}
            size="sm"
            onClick={handleAssignVet}
            disabled={assignVetMutation.isPending}
          >
            <UserPlus className="size-icon-inline" />
            {assignVetMutation.isPending
              ? "..."
              : isAssigned
                ? "Cambia vet"
                : "Assegna vet"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCloseCase}
            disabled={closeCaseMutation.isPending || isClosed}
          >
            <CircleCheck className="size-icon-inline" />
            {isClosed ? "Chiuso" : "Chiudi caso"}
          </Button>
        </>
      }
      mainClassName="bg-muted/30 p-0"
    >
      {(assignVetMutation.isError || closeCaseMutation.isError) && (
        <div role="alert" className="p-3 text-danger">
          Operazione non riuscita. Riprova: il caso non è stato modificato.
        </div>
      )}
      <div className="mail-conversation-body">
        {/* Email list / Timeline (center) */}
        <section className="mail-messages">
          <header className="mail-message-heading">
            <h2>{caseTitle}</h2>
            <p>
              {caseData.owner_name ||
                caseData.owner_email ||
                "Conversazione clinica"}{" "}
              · {allEmails.length} messaggi
            </p>
          </header>
          {/* View mode toggle */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquare className="size-icon-inline" />
              Thread ({allEmails.length} email)
            </h2>
            <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("timeline")}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === "timeline"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Timeline
              </button>
              <button
                onClick={() => {
                  setSelectedEmailId(null);
                  setViewMode("emails");
                }}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  viewMode === "emails"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Email
              </button>
            </div>
          </div>

          {viewMode === "timeline" ? (
            <ThreadTimeline
              emails={allEmails}
              onEmailClick={handleEmailClick}
              selectedEmailId={selectedEmailId}
            />
          ) : (
            (selectedEmailId
              ? allEmails.filter((email) => email.id === selectedEmailId)
              : allEmails
            ).map((email) => (
              <EmailItem
                key={email.id}
                email={email}
                onImageClick={handleImageClick}
              />
            ))
          )}
        </section>

        {/* Right panel - visible on md+ */}
        <aside
          className={`mail-patient-context ${showContext || selectedAttachment ? "is-open" : ""}`}
          aria-label="Contesto clinico"
        >
          <Button
            variant="ghost"
            size="sm"
            className="mb-3"
            onClick={() => {
              setShowContext(false);
              setSelectedAttachment(null);
            }}
          >
            Chiudi pannello
          </Button>
          <PatientCard
            patientName={caseData.patient_name}
            patientSpecies={caseData.patient_species}
            patientBreed={caseData.patient_breed}
            ownerName={caseData.owner_name}
            ownerEmail={caseData.owner_email}
            ownerPhone={caseData.owner_phone}
          />

          {selectedAttachment && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Scan className="size-icon-inline" />
                  Imaging
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedAttachment(null)}
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
              <ImagingPanel
                attachmentId={selectedAttachment.id}
                imageUrl={`/api/v1/attachments/${selectedAttachment.id}/view`}
                isDicom={selectedAttachment.is_dicom}
              />
            </div>
          )}

          {/* Case info */}
          <div className="bg-muted/30 border border-border rounded-lg p-4">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <ClipboardList className="size-icon-inline" />
              Info Caso
            </h3>

            <div className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">ID:</span>
                <span className="ml-2 text-foreground font-mono text-xs">
                  {caseData.id}
                </span>
              </div>

              {caseData.specialty && (
                <div>
                  <span className="text-muted-foreground">Specialità:</span>
                  <span className="ml-2 text-foreground">
                    {caseData.specialty}
                  </span>
                </div>
              )}

              <div>
                <span className="text-muted-foreground">Aperto:</span>
                <span className="ml-2 text-foreground">
                  {new Date(caseData.opened_at).toLocaleDateString("it-IT")}
                </span>
              </div>

              {caseData.closed_at && (
                <div>
                  <span className="text-muted-foreground">Chiuso:</span>
                  <span className="ml-2 text-foreground">
                    {new Date(caseData.closed_at).toLocaleDateString("it-IT")}
                  </span>
                </div>
              )}

              {caseData.vet_name && (
                <div>
                  <span className="text-muted-foreground">Veterinario:</span>
                  <span className="ml-2 text-primary font-medium">
                    {caseData.vet_name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      <VetAssignmentModal
        isOpen={showVetModal}
        onClose={() => setShowVetModal(false)}
        onAssign={handleVetAssign}
        isPending={assignVetMutation.isPending}
        currentVetId={caseData.vet_id}
      />

      <CaseAuditTrailModal
        caseId={caseId!}
        open={showAuditTrail}
        onOpenChange={setShowAuditTrail}
      />
    </MailDetailLayout>
  );
}
